import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Theme, useTheme } from '@mui/material/styles'
import { createStyles, makeStyles } from '@mui/styles';
import ResponsiveDrawer from "../../components/ClippedResponsiveDrawer";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import GetAppIcon from '@mui/icons-material/GetApp';
import BarChartIcon from '@mui/icons-material/BarChart';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import { AxiosRequestConfig } from "axios";
import { AXIOS_INSTANCE, BASE_URL } from "../../App";
import { useLocalStorage } from "../../utils/LocalStorageHook";
import VMDEXHeader from "./VMDEXHeader";
import { NumericUtils } from "../../utils/numericUtils";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";


interface ReportingState {
  isLoaded: boolean,
  isLoading: boolean,
  data: ReportingRow[],
  matDesc: MatDesc[],
  moneyThreshold: number,
}

interface MatDesc {
  materialNum: string,
  materialNumShort: string,
  description: string
}

interface VMDEXDetail {
  error: boolean;
  selNum: string;
  colNum: string;
  materialNum: string;
  priceVM: number;
  priceBE: number;
}

interface ReportingRow {
  status: number;
  visitDt: Date;
  shipmentNum: string;
  salesOffice: string;
  deliveringPlant: string;
  route: string;
  customerNum: string;
  assetNum: string;
  name: string;
  driverNum: string;
  driverName: string;
  confError: boolean;
  priceError: boolean;
  oos: string;
  machineErrors: string;
  cashCollected: number;
  billsExpected: number;
  coinsExpected: number;
  cashExpected: number;
  cashOverShort: number;
  cardSales: number;
  totalRefill: number;
  meterDelta: number;
  materialOverShort: number;
  totalizerDelta: number;
  changerDelta: number;
  deviceId: string;
  returncode: string;
  details: VMDEXDetail[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center",
    },
    root: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    rootContainer: {
      [theme.breakpoints.down("md")]: {
        padding: 0,
        paddingTop: theme.spacing(1),
      },
    },
    tokenBlock: {
      width: "500px",
      overflowWrap: "break-word",
    },
    typography: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
    cont: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
  })
);

async function getReportingData(salesOrg: string, deliveringPlants: string[], routes: string[], dateFrom: string, dateTo: string) {
  const configReporting: AxiosRequestConfig = {
    method: "POST",
    params: {
      clientId: "100",
      salesOrg: salesOrg,
    },
    data: {
      "deliveringPlants": deliveringPlants,
      "routes": routes,
      "dateFrom": dateFrom,
      "dateTo": dateTo,
    },
    url: `${BASE_URL}/api/reporting/vmdex`,
  };

  return await AXIOS_INSTANCE(configReporting).then((res: any) => res.data);
}

export default function ConfigurationReporting() {
  const classes = useStyles();
  const [state, setState] = useState<ReportingState>({
    isLoaded: false,
    isLoading: false,
    data: [],
    matDesc: [],
    moneyThreshold: 0
  });

  const [salesOrg,] = useLocalStorage("SETTINGS.salesOrg", "");

  const onSelectionChange = (deliveringPlants: string[], routes: string[], dateFrom: string, dateTo: string, moneyThreshold: string) => {
    const sOrg = salesOrg.toString();
    const threshold = parseFloat(moneyThreshold) || 0;
    if (deliveringPlants.length > 0) {
      setState({...state, isLoading: true});
      getReportingData(sOrg, deliveringPlants, routes, dateFrom, dateTo)
      .then((v) => {
        //console.log(v);
        setState({isLoaded: true, isLoading: false, data: v.data, matDesc: v.matDesc, moneyThreshold: threshold});
      })
    }
  }

  const handleCustNumClick = (customerNum: string) => {
    window.open(`/customer/${customerNum}`, "_blank");
  }

  const handleSalesClick = (customerNum: string) => {
    window.open(`/reporting/vmdexsales/${customerNum}`, "_blank");
  }

  const onThresholdChange = (moneyThreshold: string) => {
    const threshold = parseFloat(moneyThreshold) || 0;
    setState({...state, moneyThreshold: threshold});
  }


  const useRowStyles = makeStyles({
    root: {
      "& > *": {
        borderBottom: "unset",
      },
    },
  });

  const Row = (props: any) => {
    const { rowData } = props;
    const row:ReportingRow = rowData;
    const [open, setOpen] = React.useState(false);
    const classes = useRowStyles();

    const cashExpected = row.cashExpected.toFixed(2);
    const coinsExpected = row.coinsExpected.toFixed(2);
    const billsExpected = row.billsExpected.toFixed(2);
    const cashExp = cashExpected + " (" + coinsExpected + " + " + billsExpected + ")";
    const dexMsg = row.machineErrors.replace(",", "\n");
    let dexTT = <div style={{whiteSpace: 'pre-line'}}>{dexMsg}</div>;
    var highlight = false;
    if (Math.abs(row.cashOverShort) > state.moneyThreshold) {
      highlight = true;
    }

    return (
      <React.Fragment>
        <TableRow className={classes.root} selected={highlight}>
          <TableCell>
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell component="th" scope="row">
            {row.status === 0 && <DoneAllIcon style={{color: "green"}}/>}
            {row.status === 1 && <DoneIcon style={{color: "yellow"}}/>}
            {row.status === 2 && <ClearIcon style={{color: "red"}}/>}
          </TableCell>
          <TableCell align="right">
            {row.confError && <AccountTreeIcon color="primary"/>}
            {row.priceError && <MoneyOffIcon color="primary"/>}
            {row.machineErrors && <Tooltip title={dexTT}><ErrorOutlineIcon color="primary"/></Tooltip>}
            {row.oos && <Tooltip title={"" + row.oos}><StrikethroughSIcon color="primary"/></Tooltip>}
          </TableCell>
          <TableCell align="right">{row.visitDt ? row.visitDt.toString().split('T')[0].slice(2) : ""}</TableCell>
          <TableCell align="right">{row.shipmentNum ? row.shipmentNum.replace(/^0+/, "") : ""}</TableCell>                  
          <TableCell align="right">{row.salesOffice}</TableCell>
          <TableCell align="right">{row.deliveringPlant}</TableCell>
          <TableCell align="right">{row.route}</TableCell>
          <TableCell align="right"><Link onClick={() => handleCustNumClick(row.customerNum)}>{row.customerNum}</Link></TableCell>
          <TableCell align="right">{row.name}</TableCell>
          <TableCell align="right">{row.assetNum}</TableCell>                  
          <TableCell align="right">{row.driverNum}: {row.driverName}</TableCell>
          <TableCell align="right">$ {row.cashCollected.toFixed(2)}</TableCell>     
          <TableCell align="right">$ {cashExp}</TableCell>
          <TableCell align="right"><b>$ {row.cashOverShort.toFixed(2)}</b></TableCell>     
          <TableCell align="right">$ {row.cardSales.toFixed(2)}</TableCell>
          <TableCell align="right">$ {row.changerDelta.toFixed(2)}</TableCell>
          <TableCell align="right">{row.totalRefill}</TableCell>
          <TableCell align="right">{row.meterDelta}</TableCell>
          <TableCell align="right"><b>{row.materialOverShort.toFixed(2)}</b></TableCell>     
          <TableCell align="right">$ {row.totalizerDelta.toFixed(2)}</TableCell>
          <TableCell><BarChartIcon onClick={() => handleSalesClick(row.customerNum)} /></TableCell>
          <TableCell align="right"><Tooltip title={"RT=" + row.returncode}><Chip label={row.deviceId}/></Tooltip></TableCell>    
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={22}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">Selection #</TableCell>
                      <TableCell align="right">Column #</TableCell>
                      <TableCell align="right">Material #</TableCell>
                      <TableCell align="right">Description</TableCell>
                      <TableCell align="right">Vend Price Backend</TableCell>
                      <TableCell align="right">Vend Price Machine</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.details.map((detail, index: number) => {
                      var desc = "";
                      var num = "";
                      if (detail.materialNum) {
                        num = detail.materialNum.replace(/^0+/, "");
                      }
                      const matDesc = state.matDesc.find(elem => elem.materialNum === detail.materialNum);
                      if (matDesc) {
                        desc = matDesc.description;
                      }
                      return (
                        <TableRow key={index} selected={detail.error}>
                          <TableCell align="right">{detail.selNum}</TableCell>
                          <TableCell align="right">{detail.colNum}</TableCell>
                          <TableCell align="right">{num}</TableCell>
                          <TableCell align="right">{desc}</TableCell>
                          <TableCell align="right">{detail.priceBE ? detail.priceBE.toFixed(2) : ""}</TableCell>
                          <TableCell align="right">{detail.priceVM ? detail.priceVM.toFixed(2) : ""}</TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const DownloadButton = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("VM-DEX Report");

    worksheet.columns = [
      { header: "Status", key: "status", width: 10 },
      { header: "Visit Date", key: "visitDt", width: 10 },
      { header: "Sales Office", key: "salesOffice", width: 10 },
      { header: "Delivery Plant", key: "deliveringPlant", width: 10 },
      { header: "Route", key: "route" },
      { header: "Customer Number", key: "customerNum", width: 10 },
      { header: "Customer Name", key: "name", width: 10 },
      { header: "Asset Number", key: "assetNum", width: 10 },
      { header: "Driver Number", key: "driverNum", width: 10 },
      { header: "Driver Name", key: "driverName", width: 10 },
      { header: "Configuration Error", key: "confError", width: 10 },
      { header: "Pricing Error", key: "priceError", width: 10 },
      { header: "Machine Error", key: "machineErrors", width: 10 },
      { header: "Materials OOS", key: "oos", width: 10 },
      { header: "Cash Collected", key: "cashCollected", width: 10 },
      { header: "Cash Expected", key: "cashExpected", width: 10 },
      { header: "Bills Expected", key: "billsExpected", width: 10 },
      { header: "Coins Expected", key: "coinsExpected", width: 10 },
      { header: "Cash Over/Short", key: "cashOverShort", width: 10 },
      { header: "Card Sales", key: "cardSales", width: 10 },
      { header: "Changer +/-", key: "changerDelta", width: 10 },
      { header: "Total Refill", key: "totalRefill", width: 10 },
      { header: "Meter Increase", key: "meterDelta", width: 10 },
      { header: "Material Over/Short", key: "materialOverShort", width: 10 },
      { header: "Revenue", key: "totalizerDelta", width: 10 },
      { header: "VMDEX Device ID", key: "deviceId", width: 10 },
      { header: "Returncode", key: "returncode", width: 10 },
    ];

    const data = state.data;
    const filterdData: any = [];
    data.forEach((ele: any) => {
      let dataObject: any = {
        status: ele.status,
        visitDt: ele.visitDt.toString().split("T")[0],
        salesOffice: ele.salesOffice,
        deliveringPlant: ele.deliveringPlant,
        route: ele.route,
        customerNum: ele.customerNum,
        name: ele.name,
        assetNum: ele.assetNum,
        driverNum: ele.driverNum,
        driverName: ele.driverName,
        confError: ele.confError ? "X" : "",
        priceError: ele.priceError ? "X" : "",
        machineErrors: ele.machineErrors,
        oos: ele.oos,
        cashCollected: NumericUtils.round2Dec(ele.cashCollected),
        cashExpected: NumericUtils.round2Dec(ele.cashExpected),
        billsExpected: NumericUtils.round2Dec(ele.billsExpected),
        coinsExpected: NumericUtils.round2Dec(ele.coinsExpected),
        cashOverShort: NumericUtils.round2Dec(ele.cashOverShort),
        cardSales: NumericUtils.round2Dec(ele.cardSales),
        changerDelta: NumericUtils.round2Dec(ele.changerDelta),
        totalRefill: ele.totalRefill,
        meterDelta: ele.meterDelta,
        materialOverShort: ele.materialOverShort,
        totalizerDelta: NumericUtils.round2Dec(ele.totalizerDelta),
        deviceId: ele.deviceId,
        returncode: ele.returncode
      };

      filterdData.push(dataObject);
    });

    //inserting the rows starting from porition 2 so that headers would not be override.
    worksheet.insertRows(2, filterdData);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "VM-DEX-Report.xlsx");
  };

  let iconDesc = <div>
    <TableContainer>
      <Table size="small">
        <TableBody>
          <TableRow><TableCell style={{borderBottom: "none"}}><AccountTreeIcon style={{color: "white"}} /></TableCell><TableCell style={{borderBottom: "none"}}><Typography style={{color: "white"}}>Mapping Error</Typography></TableCell></TableRow>
          <TableRow><TableCell style={{borderBottom: "none"}}><MoneyOffIcon style={{color: "white"}} /></TableCell><TableCell style={{borderBottom: "none"}}><Typography style={{color: "white"}}>Pricing Error</Typography></TableCell></TableRow>
          <TableRow><TableCell style={{borderBottom: "none"}}><ErrorOutlineIcon style={{color: "white"}} /></TableCell><TableCell style={{borderBottom: "none"}}><Typography style={{color: "white"}}>Machine Error</Typography></TableCell></TableRow>
          <TableRow><TableCell style={{borderBottom: "none"}}><StrikethroughSIcon style={{color: "white"}} /></TableCell><TableCell style={{borderBottom: "none"}}><Typography style={{color: "white"}}>Out-Of-Stock</Typography></TableCell></TableRow>                              
        </TableBody>
      </Table>
    </TableContainer>
    </div>; 

  return (
    <ResponsiveDrawer
      title="Reporting"
    >
      <div className={classes.rootContainer}>
        <VMDEXHeader onSelectionChange={onSelectionChange} onThresholdChange={onThresholdChange}/>

        {state.isLoading &&
          <CircularProgress />
        }

        {state.isLoaded &&

          <div>
        <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell/>
              <TableCell align="right">Status</TableCell>
              <Tooltip title={iconDesc}><TableCell align="right">Errors</TableCell></Tooltip>
              <TableCell align="right">Visit Date</TableCell>
              <TableCell align="right">Shipment #</TableCell>              
              <TableCell align="right">Sales Office</TableCell>
              <TableCell align="right">Delivery Plant</TableCell>
              <TableCell align="right">Route</TableCell>
              <TableCell align="right">Customer #</TableCell>             
              <TableCell align="right">Name</TableCell>
              <TableCell align="right">Asset #</TableCell> 
              <TableCell align="right">Driver</TableCell>
              <TableCell align="right">Cash Collected</TableCell>
              <TableCell align="right">Cash Expected<br/>(Coins + Bills)</TableCell>
              <Tooltip title="Cash Over / Short"><TableCell align="right">Cash O/S</TableCell></Tooltip>
              <TableCell align="right">Card Sales</TableCell>
              <Tooltip title="Changer Fill Level Increase / Decrease"><TableCell align="right">Changer +/-</TableCell></Tooltip>
              <TableCell align="right">Refill Qty</TableCell>
              <TableCell align="right">Meter Increase</TableCell>
              <Tooltip title="Material Over / Short"><TableCell align="right">Material O/S</TableCell></Tooltip>
              <TableCell align="right">Revenue</TableCell>
              <TableCell align="right">Daily Sales</TableCell>
              <TableCell align="right">DeviceID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.data.map((row, index: number) => {
              return (
                <Row key={index} rowData={row} />
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Button component="label" style={{ marginRight: 10 }} onClick={DownloadButton}>
        <GetAppIcon/>
      </Button>
          
      </div>
    }
      </div>
    </ResponsiveDrawer>
  );
}
